import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { filestore } from '@/filestore';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onSetActiveAnimationPreset } from '../../../store/actions';
import { ARThreeScene } from '../EditorCanvas/EditorCanvas';
import EntityBoundaries from './EntityBoundaries/EntityBoundaries';
import { useGetHocSpatialProperties, useHocEntityPointerMove, useHocPointerDown, useHocPointerOut, useHocPointerUp, useShowHighlight } from './hocCustomHooks';
import { getEntityRenderOrder } from './hocUtils';
import LineBox from '../LineBox/LineBox';
const withModel3dBehaviour = (WrappedModel3d) => {
    const FuncComp = ({ enabled: entityIsEnabled = true, id }) => {
        // Selectors
        const dispatch = useDispatch();
        const entity = useSelector((state) => state.contentReducer.contentDoc.componentsById[id]);
        const projectShadowsEnabled = useSelector((state) => state.contentReducer.contentDoc.shadowsEnabled ?? true);
        const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
        const [isMouseDown, setIsMouseDown] = useState(false);
        const isEntitySelected = selectedEntityIds?.includes(id);
        // Refs
        const initialPosRef = useRef(null); // Used to calculate pointer offset in pointerMove
        const pointerStageRef = useRef(null);
        // Entity properties to pass to wrapped component
        const renderOrder = getEntityRenderOrder(entity);
        const { opacity, originalBBox, idleAnimation, idlePoseTime, castShadow = false, receiveShadow = false } = entity;
        const { scale, position, rotation } = useGetHocSpatialProperties(entity);
        const [model3dUrl, setModel3dUrl] = useState();
        const [model3dHasError, setModel3dHasError] = useState(false);
        const [isHovered, setIsHovered] = useState(false);
        // const onPointerOver = useHocPointerOver(entity, () => setIsHovered(true), () => setIsHovered(false));
        const onPointerOut = useHocPointerOut(entity, () => setIsHovered(false));
        const onPointerMove = useHocEntityPointerMove(entity, () => setIsHovered(true), () => setIsHovered(false));
        useEffect(() => {
            if (!isEntitySelected)
                setIsHovered(false);
            const onMouseDown = () => setIsMouseDown(true);
            const onMouseUp = () => setIsMouseDown(false);
            document.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mouseup', onMouseUp);
            return () => {
                document.removeEventListener('mouseDown', onMouseDown);
                document.removeEventListener('onMouseUp', onMouseUp);
            };
        }, [isEntitySelected]);
        const showHighlight = useShowHighlight(id, isHovered);
        useEffect(() => {
            const fsModel = filestore.load(entity.filestoreId);
            fsModel.getUrl()
                .then(url => setModel3dUrl(url))
                .catch(() => setModel3dHasError(true));
        }, [entity.filestoreId]);
        // Pointer events
        const onPointerDown = useHocPointerDown({ pointerStageRef, initialPosRef, entity });
        const onPointerUp = useHocPointerUp({ pointerStageRef, initialPosRef, entity });
        const _onPointerDown = (e) => {
            dispatch(onSetActiveAnimationPreset(false));
            const threeEntity = ARThreeScene.getObjectByName(id);
            const threeEntityInner = threeEntity.children[0];
            threeEntity?.position.set(...position);
            threeEntity?.rotation.set(...rotation);
            threeEntityInner?.scale.set(1, 1, 1);
            if (entityIsEnabled)
                onPointerDown(e);
        };
        // If idle pose time is undefined then if there is an idle animation selected, use it's first frame on canvas
        const displayIdlePoseTime = idlePoseTime === undefined ? idleAnimation ? 0 : undefined : idlePoseTime;
        return (_jsx(_Fragment, { children: _jsx(EntityBoundaries, { forceError: model3dHasError, forceLoading: typeof model3dUrl === 'undefined', position: position, rotation: rotation, scale: scale, renderOrder: renderOrder, onPointerDown: _onPointerDown, onPointerUp: onPointerUp, label: `Model 3D component ${id} render order ${renderOrder}`, children: _jsxs(_Fragment, { children: [_jsx(WrappedModel3d, { id: id, scale: scale, rotation: rotation, position: position, model3dUrl: model3dUrl, renderOrder: renderOrder, originalBBox: originalBBox, opacity: opacity, castShadow: castShadow && projectShadowsEnabled, receiveShadow: receiveShadow && projectShadowsEnabled, onPointerDown: _onPointerDown, onPointerOut: entityIsEnabled && !isEntitySelected && !isMouseDown ? onPointerOut : undefined, onPointerMove: entityIsEnabled && !isEntitySelected && !isMouseDown ? onPointerMove : undefined, idleAnimation: idleAnimation, idlePoseTime: displayIdlePoseTime, enableBVH: false }), showHighlight && _jsx("group", { position: position, rotation: rotation, children: _jsx(LineBox, { renderOrder: renderOrder, rotation: [0, 0, 0], scale: [scale[0] / 2, scale[1] / 2, scale[2] / 2], color: [66, 135, 245, 1], lineWidth: 6 }) })] }) }) }));
    };
    return FuncComp;
};
export default withModel3dBehaviour;
